// Shared plumbing for the landing page's live sections (hero pane, tasks
// stream, memory loop). Ported from the one-page design artifact at
// apps/frontend/design/legix-website-v2/Website.dc.html (the `Component`
// class in its data-dc-script) — the logic is kept as close to the artifact
// as TypeScript allows so the played sequences stay byte-comparable.
//
// The demo corpus itself ships VERBATIM from the artifact as plain scripts
// (public/landing/**, loaded by the landing page) that hang data off window:
// SEED (client + chart + transactions), STREAM (the reasoning entries),
// TASKMEM (the cited memories), MEMLOOP (the memory-loop beats). Nothing in
// these types invents shape — they describe exactly what those files return.

export interface SeedAccount {
  no: string
  name: string
  parent?: string
  type: string
  suspense?: boolean
}

export interface SeedSplitLeg {
  label: string
  to: string
  amt: number
}

export interface SeedTxn {
  id: string
  wave: number
  from: string
  date: string
  dir: 'debit' | 'credit'
  amt: number
  raw: string
  payee?: string
  to?: string
  state: 'applied' | 'needsyou'
  cites?: string[]
  reason?: string
  split?: SeedSplitLeg[]
  correctedTo?: string
  correctedBy?: string
}

export interface SeedStaff {
  id: string
  name: string
  first: string
  role: string
}

export interface SeedWave {
  n: number
  account: string
  added: string
}

export interface Seed {
  firm: { name: string; id: string }
  staff: SeedStaff[]
  client: { id: string; name: string; short: string; trade: string; where?: string }
  accounts: SeedAccount[]
  transactions: SeedTxn[]
  waves: SeedWave[]
  acct(no: string): SeedAccount | null
  label(no: string): string
  fullName(a: SeedAccount): string
  inWave(n: number): SeedTxn[]
  sum(rows: SeedTxn[]): number
  money(v: number): string
}

export interface StreamEvidence {
  label?: string
  text?: string
  to?: string
}

export interface StreamPart {
  t?: string
  cite?: string
  evidence?: StreamEvidence
  change?: string
}

export interface StreamEntry {
  id: string
  kind: string
  beat?: string
  actor?: string
  cites?: string[]
  txns?: string[]
  parts?: StreamPart[]
}

export interface TaskMemory {
  id: string
  scope: string
  body: string
}

export interface TaskMem {
  get(id: string): TaskMemory | null
}

export interface MemLoopBeat {
  id: string
  month?: string
  raw?: string
  payee?: string
  date?: string
  amt?: number
  to?: string
  cite?: string | null
  reason?: string
  kind?: string
  memory?: string
}

export interface MemLoop {
  memory: {
    id: string
    scope: string
    scopeLabel: string
    author: string
    added: string
    body: string
  }
  beats: MemLoopBeat[]
  acct(no: string): string
  money(n: number): string
}

declare global {
  interface Window {
    SEED?: Seed
    STREAM?: { entries: StreamEntry[] }
    TASKMEM?: TaskMem
    MEMLOOP?: MemLoop
  }
}

// The data scripts are separate requests, so any caller must be ready for null
// and re-ask (the artifact's drivers poll with a 60ms retry — keep doing that).
export const seed = (): Seed | null =>
  typeof window === 'undefined' ? null : (window.SEED ?? null)
export const streamEntries = (): StreamEntry[] =>
  typeof window === 'undefined' ? [] : (window.STREAM?.entries ?? [])
export const taskMem = (): TaskMem | null =>
  typeof window === 'undefined' ? null : (window.TASKMEM ?? null)
export const memLoop = (): MemLoop | null =>
  typeof window === 'undefined' ? null : (window.MEMLOOP ?? null)

export const prefersReduced = (): boolean =>
  typeof window !== 'undefined' &&
  !!window.matchMedia &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches

/** Timer pool with a generation token: `clear()` bumps the token so pending
 *  callbacks from a previous generation are dropped (the artifact's `at()`). */
export class Timers {
  private tok = 0
  private timers: ReturnType<typeof setTimeout>[] = []

  at(ms: number, fn: () => void): void {
    const mine = this.tok
    this.timers.push(
      setTimeout(() => {
        if (mine === this.tok) fn()
      }, ms),
    )
  }

  clear(): void {
    this.tok++
    this.timers.forEach(clearTimeout)
    this.timers = []
  }
}

/** The parts an entry's reasoning renders. `change` parts are vestigial — the
 *  change card they drove was retired (D30). */
export const rparts = (e: StreamEntry): StreamPart[] => (e.parts ?? []).filter((p) => !p.change)

/* The strip is one or two lines, so a cut mid-clause reads as a bug rather
   than a summary. Preference order: end on a sentence that fits whole; else on
   a clause boundary (— ; : ,) with an ellipsis; else on a word. A trailing
   colon is dropped — it promises a quote the strip does not show. */
export function trimReason(s: string, max = 118): string {
  s = (s || '').trim()
  if (s.length <= max) return s.replace(/:$/, '.')
  const head = s.slice(0, max + 1)
  const sentence = head.search(/[.!?](?=\s|$)(?![^]*[.!?](?=\s|$))/)
  if (sentence > 40) return s.slice(0, sentence + 1)
  const clause = Math.max(
    head.lastIndexOf('—'),
    head.lastIndexOf(';'),
    head.lastIndexOf(':'),
    head.lastIndexOf(','),
  )
  if (clause > 40) return s.slice(0, clause).replace(/[\s]+$/, '') + '…'
  return s.slice(0, max).replace(/[\s]+\S*$/, '') + '…'
}

export interface Token {
  v: string
  cls: string
}

/** Markdown resolved into a flat token list, each token carrying only a class. */
export function tokenize(text: string): Token[] {
  const out: Token[] = []
  let buf = ''
  let i = 0
  const flush = () => {
    if (buf) {
      out.push({ v: buf, cls: '' })
      buf = ''
    }
  }
  const push = (v: string, cls: string) => {
    if (v) out.push({ v, cls })
  }
  const t = text || ''
  while (i < t.length) {
    const ch = t[i]
    if (ch === '*' && t[i + 1] === '*') {
      const e = t.indexOf('**', i + 2)
      if (e > -1) {
        flush()
        push(t.slice(i + 2, e), 'ts-strong')
        i = e + 2
        continue
      }
    }
    if (ch === '*') {
      const e = t.indexOf('*', i + 1)
      if (e > -1) {
        flush()
        push(t.slice(i + 1, e), 'ts-em')
        i = e + 1
        continue
      }
    }
    buf += ch
    i++
  }
  flush()
  return out
}

/** One span per character so arriving text FADES in. Styled tokens stay whole. */
export function units(tokens: Token[]): Token[] {
  const out: Token[] = []
  for (const t of tokens) {
    if (t.cls) {
      out.push({ v: t.v, cls: t.cls + ' ts-w' })
      continue
    }
    for (const ch of t.v) out.push({ v: ch, cls: 'ts-w' })
  }
  return out
}

export interface PaneMetrics {
  row: number
  box: number
}

/* Row and window height, measured once from the rendered list. THE MEASUREMENT
   MUST NOT BE TAKEN UNSTYLED (D49): tasks.css is a separate request, so refuse
   to measure until the box is actually at the stylesheet's height. Returning
   null is free — the caller leaves the track where it is and asks again next
   render. Cache is PER PANE (keyed by selector) and re-taken whenever the box
   height changes (a resize across the breakpoint). */
export function paneMetrics(cache: Record<string, PaneMetrics>, sel: string): PaneMetrics | null {
  if (typeof document === 'undefined') return null
  const box = document.querySelector(sel)
  const row = box?.querySelector<HTMLElement>('.ts-row')
  if (!box || !row || !row.offsetHeight) return null
  const h = box.clientHeight
  if (h < row.offsetHeight * 3) return null
  const hit = cache[sel]
  if (hit && hit.box === h && hit.row === row.offsetHeight) return hit
  cache[sel] = { row: row.offsetHeight, box: h }
  return cache[sel]
}

export function acctName(D: Seed, no: string): string {
  const a = D.acct(no)
  return a ? D.fullName(a) : no
}

export function scopeLabel(D: Seed | null, m: TaskMemory | null): string {
  if (!m) return 'Memory'
  if (m.scope === 'firm') return 'Firm memory'
  const name = D && D.client.id === m.scope ? D.client.short : m.scope
  return name + ' memory'
}
